<template>
  <div
    v-if="!isLoading2"
    class="section"
  >
    <div class="columns is-centered">
      <div class="column is-11">
        <div class="columns">
          <div class="column has-text-centered">
            <h2 class="title is-2">
              Business Interest Transferred
            </h2>
            <div class="subtitle is-2">
              <div v-html="businessIntTransText ? businessIntTransText[0].description : ''" />
            </div>
            <checkbox-adv
              id="nothing-to-report"
              v-model="nothingToReport"
              :value="true"
            >
              Check this box if you have no <strong>Business Interest Transferred</strong> to report.
            </checkbox-adv>
          </div>
        </div>
        <div
          v-if="!nothingToReport"
          class="section"
        >
          <validation-observer
            ref="form-business-interests-transferred"
            tag="form"
          >
            <div class="columns is-multiline">
              <div class="column is-5">
                <textbox-vee
                  id="business-name"
                  v-model="newItem.name"
                  placeholder="Business Name"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-4">
                <textbox-vee
                  id="interest-held"
                  v-model="newItem.interestHeld"
                  type="number"
                  placeholder="Interest Held (xx.xx%)"
                  rules="percentage|required"
                  required
                />
              </div>
              <div class="column is-3">
                <date-picker
                  id="transfer-date"
                  v-model="newItem.transferDate"
                  :required="true"
                  placeholder="Transfer Date"
                  read-only
                />
              </div>
              <div class="column is-4">
                <textbox
                  id="business-address"
                  v-model="newItem.streetAddress"
                  placeholder="Business Address"
                />
              </div>
              <div class="column is-3">
                <textbox
                  id="business-city"
                  v-model="newItem.city"
                  placeholder="City"
                />
              </div>
              <div class="column is-3">
                <dropdown
                  v-model="newItem.state"
                  :options="states"
                  placeholder="State"
                />
              </div>
              <div class="column is-2">
                <textbox-vee
                  id="business-zip-code"
                  v-model="newItem.zip"
                  placeholder="Zip Code"
                  rules="zipCode"
                />
              </div>
              <div class="column is-6">
                <textbox-vee
                  id="transferee-name"
                  v-model="newItem.transfereeName"
                  placeholder="Transferee Name"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-6">
                <textbox
                  id="transferee-relationship"
                  v-model="newItem.transfereeRelationship"
                  placeholder=" Transferee Relationship"
                />
              </div>
              <div class="column is-4">
                <textbox
                  id="transferee-street-address"
                  v-model="newItem.transfereeStreetAddress"
                  placeholder="Transferee Address"
                />
              </div>
              <div class="column is-3">
                <textbox
                  id="transferee-city"
                  v-model="newItem.transfereeCity"
                  placeholder="City"
                />
              </div>
              <div class="column is-3">
                <dropdown
                  v-model="newItem.transfereeState"
                  :options="states"
                  placeholder="State"
                />
              </div>
              <div class="column is-2">
                <textbox-vee
                  id="transferee-zip-code"
                  v-model="newItem.transfereeZip"
                  placeholder="Zip Code"
                  rules="zipCode"
                />
              </div>
              <div class="column is-12 has-text-centered">
                <button
                  id="action-btn"
                  type="button"
                  class="button is-secondary"
                  @click="trigerNewItemAction"
                >
                  {{ tableAction }} Business Interest Transfer
                </button>
              </div>
            </div>
          </validation-observer>
          <div
            v-if="items"
            class="columns"
          >
            <div class="column">
              <v-client-table
                ref="vc-table"
                :data="allItems"
                :columns="table"
                :options="tableOptions"
              >
                <div
                  slot="edit"
                  slot-scope="props"
                >
                  <button
                    :id="`edit-btn-${props.row.id}`"
                    type="button"
                    title="Edit"
                    aria-label="Edit item"
                    class="button is-small is-warning"
                    @click.prevent="editItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-pencil-alt"
                    />
                  </button>
                </div>
                <div
                  slot="delete"
                  slot-scope="props"
                >
                  <button
                    :id="`delete-btn-${props.row.id}`"
                    type="button"
                    title="Delete"
                    aria-label="Delete item"
                    class="button is-small is-danger"
                    @click.prevent="deleteItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-trash"
                    />
                  </button>
                </div>
                <div
                  slot="clone"
                  slot-scope="props"
                >
                  <button
                    :id="`clone-btn-${props.row.id}`"
                    type="button"
                    title="Clone"
                    aria-label="Clone item"
                    class="button is-small is-secondary"
                    @click.prevent="cloneItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-copy"
                    />
                  </button>
                </div>
                <div
                  slot="interestHeld"
                  slot-scope="props"
                >
                  {{ props.row.interestHeld }}%
                </div>
              </v-client-table>
            </div>
          </div>
        </div>
        <div class="columns has-text-centered">
          <div class="column">
            <button
              id="save-btn"
              type="button"
              class="button is-primary"
              :class="isSaving ? 'is-loading' : ''"
              :disabled="isSaving"
              @click="saveAndContinueCheck('Business Interest Transferred')"
            >
              Save and Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { ClientTable } from 'vue-tables-2';
import { filerScreenHelpers } from '@/mixins/filerScreen';
import { controlValueHelpers } from '@/mixins/controlValue';
import CheckboxAdv from '@/components/phila-ui/CheckboxAdv';
import DatePicker from '@/components/phila-ui/DatePicker';
import { Textbox, Dropdown } from '@phila/phila-ui';
import { withValidation } from 'vee-validate';

const TextboxVee = withValidation(Textbox);

Vue.use(ClientTable, {}, false, 'bulma');

export default {
  name: 'BusinessInterestTransfer',
  components: {
    CheckboxAdv,
    TextboxVee,
    Textbox,
    Dropdown,
    DatePicker,
  },
  mixins: [
    filerScreenHelpers,
    controlValueHelpers,
  ],
  data() {
    return {
      triggeredOnce: false,
      currentFiling: null,
      items: [],
      isSaving: false,
      newItem: {
        name: '',
        interestHeld: '',
        transferDate: '',
        streetAddress: '',
        city: '',
        state: '',
        zip: '',
        transfereeName: '',
        transfereeRelationship: '',
        transfereeStreetAddress: '',
        transfereeCity: '',
        transfereeState: '',
        transfereeZip: '',
      },
      nothingToReport: false,
      table: [
        'name',
        'interestHeld',
        'transfereeName',
        'edit',
        'clone',
        'delete',
      ],
      tableOptions: {
        columnsClasses: {
          'edit': 'actions has-text-centered',
          'delete': 'actions has-text-centered',
          'clone': 'actions has-text-centered',
        },
        pagination: {
          dropdown: false,
          edge: true,
        },
        sortable: [],
        perPage: 50,
        perPageValues: [],
        filterable: false,
        headings: {
          name: 'Business Name',
          interestHeld: 'Interest Held (%)',
          transfereeName: 'Transferee Name',
        },
      },
    };
  },
  computed: {
    states () {
      return this.controlValueDropdown('state');
    },
    businessIntTransText () {
      return this.$store.getters[ 'admin/activeControlValues' ]('businessIntTransText');
    },
  },
  watch: {
    items: {
      deep: true,
      handler: function (items) {
        items.forEach(item => {
          if (item.interestHeld) {
            item.interestHeld = parseFloat(item.interestHeld);
          }
        });
      },
    },
  },
  async created () {
    await this.initialFetch();
    await this.initialAssign();
    this.$store.commit('loading', false);
  },
  methods: {
    hasNothingToReport () {
      if (this.items.length === 0 && this.currentFiling.businessInttTransCompleted === 'Y') {
        this.nothingToReport = true;
      }
    },
    getCurrentBusinessInterestTransfers () {
      return JSON.parse(JSON.stringify(this.$store.state.businessInterestTransfer.businessInterestTransfers));
    },
    async initialAssign () {
      this.currentFiling = this.getCurrentFiling();
      this.items = this.getCurrentBusinessInterestTransfers();
      this.fixDate();
      this.hasNothingToReport();
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'state'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'businessIntTransText'));
      promises.push(this.$store.dispatch('businessInterestTransfer/fetchBusinessInterestsTransferred'));
      await Promise.all(promises);
      this.ready = true;
    },
    fixDate () {
      this.items.forEach(item => item.transferDate === null ? item.transferDate = '' : item.transferDate);
    },
    saveAndContinue () {
      let promises = [];

      const col = 'businessInttTransCompleted';
      const endpoint = 'business-interests-transferred';

      let filing = {
        id: this.currentFiling.id,
      };

      filing[col] = this.currentFiling[col];

      //V-model.number won't work here, so we need to parse it ourselves
      this.items.forEach(item => {
        item.interestHeld = parseFloat(item.interestHeld);
        item.transferDate = this.formatDate(item.transferDate, 'YYYY-MM-DD');
      });

      let isDeletingAll = this.isDeletingAllItems();

      if (this.nothingToReport) {
        filing[col] = 'Y';
      } else {
        if (this.items.length === 0) {
          filing[col] = 'N';
        } else {
          promises.push(this.$store.dispatch('filing/saveFilingScreen', {
            items: this.items,
            endpoint,
          }));
          if (isDeletingAll === true) {
            filing[col] = 'N';
          } else {
            filing[col] = 'Y';
          }

        }
      }

      let self = this;

      promises.push(this.$store.dispatch('filing/saveFiling', filing).then(() => {
        if (filing[col] === 'Y' && this.nothingToReport === true) {

          self.items.forEach(item => {
            self.deleteItem(item.id);
          }, self);

          self.$store.dispatch('filing/saveFilingScreen', {
            items: self.items,
            endpoint,
          });

        }
      }));


      Promise.all(promises).then(() => {
        this.$router.push({ name: this.nextFilingScreen });
      });
    },
    async trigerNewItemAction () {
      const itemRef = this.$refs['form-business-interests-transferred'];
      const isValid = await itemRef.validate();
      this.triggeredOnce = true;

      if (!isValid) {
        return false;
      }

      if (this.tableAction === 'Add') {
        this.addNewItem();
      }
      if (this.tableAction === 'Update') {
        this.updateItem();
      }
      itemRef.reset();
      this.triggeredOnce = false;
    },
  },
};
</script>
